import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { CRYPTO_METHODS } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import {
  Trash2, Minus, Plus, Copy, Check, ShoppingBag, Mail, Bitcoin, Zap,
  ArrowLeft, Loader2, ShieldCheck, Info, Clock, Tag, X as XIcon,
} from 'lucide-react';
import BrandImage from '../components/BrandImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { items, removeItem, updateQty, faceTotal, serviceFee, total, clearCart } = useCart();
  const { user } = useAuth();
  const { formatPrice, isUsd } = useCurrency();
  const [email, setEmail] = useState(() => {
    // Try to prefill from persisted auth state so user doesn't have to wait for /auth/me to resolve
    try {
      const saved = JSON.parse(localStorage.getItem('tokengate_auth') || 'null');
      return saved?.user?.email || '';
    } catch { return ''; }
  });
  const [method, setMethod] = useState(CRYPTO_METHODS[0].id);
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState(null);
  const [minUsd, setMinUsd] = useState(null); // NOWPayments min for selected crypto
  const [promoInput, setPromoInput] = useState('');
  const [promoState, setPromoState] = useState({ status: 'idle', code: null, discount_pct: 0, description: '', error: null });
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => { if (user?.email) setEmail(user.email); }, [user?.email]);

  // If the user has saved a default payment coin in Preferences, preselect it here.
  // Only apply once, on mount, and only if the saved coin is a valid known method.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user/preferences`);
        const saved = data?.default_pay_currency;
        if (!cancelled && saved && CRYPTO_METHODS.some(m => m.id === saved)) {
          setMethod(saved);
        }
      } catch { /* no-op — falls back to default first method */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const selectedMethod = CRYPTO_METHODS.find(m => m.id === method);
  const belowMin = minUsd != null && total > 0 && total < minUsd;

  // Discount from an applied promo code — applies ONLY to the service fee
  // (not the gift-card face value). Example: $100 card + $10 fee, TG5 (5%)
  // → 5% of $10 = $0.50 off → final total $109.50.
  const discountUsd = promoState.status === 'applied'
    ? +((serviceFee) * (promoState.discount_pct / 100)).toFixed(2)
    : 0;
  const totalAfterDiscount = Math.max(0.01, +(total - discountUsd).toFixed(2));

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    if (!email) {
      setPromoState({ status: 'error', code: null, discount_pct: 0, description: '', error: 'Enter your email first so we can check eligibility.' });
      return;
    }
    setPromoState(s => ({ ...s, status: 'checking', error: null }));
    try {
      const { data } = await axios.post(`${API}/promo/validate`, { code, email });
      setPromoState({ status: 'applied', code: data.code, discount_pct: data.discount_pct, description: data.description || '', error: null });
      toast({ title: 'Promo applied', description: `${data.discount_pct}% off your service fee. ${data.description || 'Enjoy!'}` });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setPromoState({ status: 'error', code: null, discount_pct: 0, description: '', error: typeof detail === 'string' ? detail : 'This code is not valid.' });
    }
  };

  const clearPromo = () => {
    setPromoInput('');
    setPromoState({ status: 'idle', code: null, discount_pct: 0, description: '', error: null });
  };

  // Fetch NOWPayments minimum for the selected crypto so we can warn early
  useEffect(() => {
    if (step !== 1 || !method) { setMinUsd(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payments/min-amount`, { params: { currency: method } });
        if (!cancelled) setMinUsd(data?.min_usd ?? null);
      } catch { if (!cancelled) setMinUsd(null); }
    })();
    return () => { cancelled = true; };
  }, [method, step]);

  // Poll status once we have an order — 1s + refresh on window focus keeps this real-time.
  // Backend receives NOWPayments IPN webhooks the moment the tx is picked up by their
  // infrastructure, so the DB is already up-to-date by the time we poll.
  useEffect(() => {
    if (!order?.id || step !== 2) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${order.id}/status`);
        if (cancelled) return;
        setStatus(data);
        if (['paid', 'delivered'].includes(data.status)) {
          setTimeout(() => navigate(`/order/${order.id}`), 800);
        }
      } catch (e) {}
    };
    poll();
    const id = setInterval(poll, 1000);
    const onFocus = () => poll();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [order?.id, step, navigate]);

  const proceed = async () => {
    if (!email || !email.includes('@')) { toast({ title: 'Enter a valid email' }); return; }
    if (items.length === 0) { toast({ title: 'Your cart is empty' }); return; }

    setSubmitting(true);
    try {
      const payload = {
        email,
        crypto_currency: method,
        items: items.map(it => ({
          brand_id: it.brandId, brand_name: it.brandName, brand_slug: it.brandSlug || null,
          brand_color: it.brandColor || null, denomination: it.denomination,
          quantity: it.quantity, country: it.country || null,
        })),
        promo_code: promoState.status === 'applied' ? promoState.code : null,
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      setOrder(data);
      clearCart();
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend returns a structured object for below-minimum-amount errors
      if (detail && typeof detail === 'object' && detail.code === 'amount_too_small') {
        toast({
          title: 'Amount too small',
          description: detail.message || 'Please add more items or switch to Bitcoin.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Order failed',
          description: (typeof detail === 'string' ? detail : detail?.message) || 'Please try again in a moment.',
          variant: 'destructive',
        });
      }
    } finally { setSubmitting(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (items.length === 0 && step === 1) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-5">
          <ShoppingBag className="w-7 h-7 text-neutral-500" />
        </div>
        <h1 className="font-display text-2xl font-bold">Your cart is empty</h1>
        <p className="text-neutral-600 mt-2">Browse hundreds of brands and pay with your crypto.</p>
        <Link to="/browse"><Button className="mt-6 bg-[#8247E5] hover:bg-[#6d33cc]">Start shopping</Button></Link>
      </div>
    );
  }

  const statusLabel = {
    pending_payment: { label: 'Waiting for payment', color: 'text-amber-700 bg-amber-50 border-amber-200' },
    confirming:      { label: 'Confirming on-chain', color: 'text-sky-700 bg-sky-50 border-sky-200' },
    partially_paid:  { label: 'Partial payment. Send remaining', color: 'text-orange-700 bg-orange-50 border-orange-200' },
    paid:            { label: 'Payment received!',  color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    delivered:       { label: 'Delivered',          color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    expired:         { label: 'Payment expired',    color: 'text-rose-700 bg-rose-50 border-rose-200' },
    failed:          { label: 'Payment failed',     color: 'text-rose-700 bg-rose-50 border-rose-200' },
  }[status?.status || order?.status || 'pending_payment'] || { label: 'Waiting', color: 'text-neutral-600 bg-neutral-50 border-neutral-200' };

  // Partial payment math — how much crypto is still owed?
  const actuallyPaid = Number(status?.last_np_payload?.actually_paid || order?.last_np_payload?.actually_paid || 0);
  const expectedPay = Number(order?.payment_amount_crypto || 0);
  const remainingCrypto = Math.max(0, +(expectedPay - actuallyPaid).toFixed(8));
  const isPartial = (status?.status || order?.status) === 'partially_paid';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {step === 2 && (
        <button onClick={() => setStep(1)} className="mb-4 flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      )}
      <h1 className="font-display text-3xl font-bold mb-1">{step === 1 ? 'Checkout' : 'Send your payment'}</h1>
      <p className="text-sm text-neutral-600 mb-8">
        {step === 1
          ? (user ? 'Review your cart and pick a crypto to pay with.' : 'Guest checkout, no account needed. Or sign in from the top-right.')
          : 'Send the exact crypto amount to the address below. Codes will be emailed as soon as it arrives.'}
      </p>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {step === 1 ? (
            <>
              {/* Cart */}
              <div className="bg-white rounded-2xl border border-neutral-200 divide-y">
                {items.map(it => (
                  <div key={it.key} className="p-4 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center shrink-0 overflow-hidden" style={{ background: `${it.brandColor}12` }}>
                      <BrandImage brand={{ name: it.brandName, slug: it.brandSlug, domain: it.brandDomain, color: it.brandColor }}
                        className="max-h-10 max-w-10 object-contain"
                        tileClassName="w-10 h-10 rounded-md text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{it.brandName}</div>
                      <div className="text-xs text-neutral-500">{formatPrice(it.denomination)} · {it.country}</div>
                    </div>
                    <div className="flex items-center border border-neutral-200 rounded-lg">
                      <button onClick={() => updateQty(it.key, it.quantity - 1)} className="px-2 h-9"><Minus className="w-3.5 h-3.5" /></button>
                      <div className="w-8 text-center text-sm font-semibold">{it.quantity}</div>
                      <button onClick={() => updateQty(it.key, it.quantity + 1)} className="px-2 h-9"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="w-24 text-right font-semibold">{formatPrice(it.denomination * it.quantity)}</div>
                    <button onClick={() => removeItem(it.key)} className="text-neutral-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Email */}
              <div className="bg-white rounded-2xl border border-neutral-200 p-6">
                <label className="text-sm font-semibold mb-2 flex items-center gap-2"><Mail className="w-4 h-4" /> Where should we send your codes?</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11" />
                <p className="text-xs text-neutral-500 mt-2">
                  {user ? 'Your gift codes will also appear in your account.' : 'No account needed. But if you sign up first, you get order history for free.'}
                </p>
              </div>

              {/* Crypto method */}
              <div className="bg-white rounded-2xl border border-neutral-200 p-6">
                <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Bitcoin className="w-4 h-4 text-[#8247E5]" /> Pay with</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CRYPTO_METHODS.map(m => {
                    const active = method === m.id;
                    return (
                      <button key={m.id} data-testid={`pay-method-${m.id}`} onClick={() => setMethod(m.id)}
                        className={`text-left p-3 rounded-xl border-2 transition ${active ? 'border-[#8247E5] bg-violet-50' : 'border-neutral-200 bg-white hover:border-neutral-400'}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: m.color }}>
                            {m.ticker.split(' ')[0].slice(0, 3)}
                          </div>
                          <div>
                            <div className="text-sm font-semibold">{m.name}</div>
                            {m.instant && <div className="text-[10px] text-[#8247E5] flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" /> Instant</div>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {belowMin && (
                  <div data-testid="min-amount-warning" className="mt-4 flex items-start gap-2 p-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 text-xs">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">Heads-up: {selectedMethod?.name} typically needs ~{formatPrice(minUsd)}</div>
                      <div className="mt-1 leading-relaxed">
                        Your order is <b>{formatPrice(total)}</b>. It may still work, but if the payment provider rejects it we'll let you know instantly. You can then add more items or
                        {' '}<button type="button" onClick={() => setMethod('btc')} className="underline font-semibold text-amber-900 hover:text-amber-700" data-testid="switch-to-btc">switch to Bitcoin</button>.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Promo code */}
              <div className="bg-white rounded-2xl border border-neutral-200 p-6">
                <label className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#8247E5]" /> Have a promo code?
                </label>
                {promoState.status === 'applied' ? (
                  <div data-testid="promo-applied" className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-emerald-800 truncate">
                          {promoState.code} · {promoState.discount_pct}% off service fee
                        </div>
                        {promoState.description && (
                          <div className="text-[11px] text-emerald-700/80 truncate">{promoState.description}</div>
                        )}
                      </div>
                    </div>
                    <button onClick={clearPromo} data-testid="promo-clear" className="h-8 w-8 rounded-lg hover:bg-emerald-100 flex items-center justify-center text-emerald-700 shrink-0" aria-label="Remove promo code">
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Input
                        data-testid="promo-input"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }}
                        placeholder="e.g. TG5"
                        className="h-11 font-mono tracking-wider"
                        maxLength={24}
                      />
                      <Button
                        data-testid="promo-apply"
                        onClick={applyPromo}
                        disabled={!promoInput || promoState.status === 'checking'}
                        variant="outline"
                        className="h-11 px-5"
                      >
                        {promoState.status === 'checking' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                    {promoState.status === 'error' && (
                      <p data-testid="promo-error" className="text-xs text-rose-600 mt-2 flex items-center gap-1"><Info className="w-3 h-3" /> {promoState.error}</p>
                    )}
                    {promoState.status === 'idle' && (
                      <p className="text-xs text-neutral-500 mt-2">First time here? Try <b>TG5</b> for 5% off your service fee.</p>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <PaymentPanel
              order={order}
              status={status}
              method={method}
              isPartial={isPartial}
              actuallyPaid={actuallyPaid}
              expectedPay={expectedPay}
              remainingCrypto={remainingCrypto}
              copied={copied}
              onCopy={copy}
              formatPrice={formatPrice}
              isUsd={isUsd}
            />
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-neutral-200 p-6 sticky top-24">
            <h3 className="font-semibold mb-4">Order summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-neutral-600">Items</span><span>{step === 1 ? items.length : (order?.items?.length || 0)}</span></div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Gift card value</span>
                <span data-testid="summary-face-total">{formatPrice(step === 1 ? faceTotal : (order?.face_total_usd ?? order?.subtotal_usd / 1.1) || 0, { decimals: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 flex items-center gap-1">Service fee <span className="text-[10px] font-semibold text-[#8247E5]">10%</span></span>
                <span data-testid="summary-service-fee">{formatPrice(step === 1 ? serviceFee : (order?.service_fee_usd ?? (order?.subtotal_usd || 0) - ((order?.subtotal_usd || 0) / 1.1)) || 0, { decimals: 2 })}</span>
              </div>
              {(step === 1 && promoState.status === 'applied' && discountUsd > 0) || (step === 2 && order?.discount_usd > 0) ? (
                <div className="flex justify-between text-emerald-700" data-testid="summary-discount">
                  <span className="flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Promo {step === 1 ? promoState.code : order?.promo_code} · {step === 1 ? promoState.discount_pct : Math.round(((order?.discount_usd || 0) / Math.max(order?.service_fee_usd || 1, 0.01)) * 100)}% off service fee
                  </span>
                  <span>−{formatPrice(step === 1 ? discountUsd : (order?.discount_usd || 0), { decimals: 2 })}</span>
                </div>
              ) : null}
              <div className="flex justify-between"><span className="text-neutral-600">Network fee</span><span className="text-green-600">Paid by you at wallet</span></div>
              <div className="border-t border-neutral-200 pt-3 mt-3 flex justify-between font-bold text-base">
                <span>Total to pay</span>
                <span data-testid="summary-total">{formatPrice(step === 1 ? totalAfterDiscount : order?.subtotal_usd || 0, { decimals: 2 })}</span>
              </div>
              {!isUsd && (
                <div className="text-[11px] text-neutral-500 pt-1">
                  ≈ ${(step === 1 ? totalAfterDiscount : order?.subtotal_usd || 0).toFixed(2)} USD · charged in crypto equivalent
                </div>
              )}
              {step === 2 && order?.payment_amount_crypto && (
                <div className="text-xs text-neutral-500 flex items-center gap-1">
                  <Bitcoin className="w-3 h-3 text-[#8247E5]" /> {order.payment_amount_crypto} {(order.payment_pay_currency || method).toUpperCase()}
                </div>
              )}
            </div>
            {step === 1 && (
              <Button data-testid="pay-btn" onClick={proceed} disabled={submitting} className="w-full mt-5 h-12 bg-[#8247E5] hover:bg-[#6d33cc] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay ${formatPrice(totalAfterDiscount, { decimals: 2 })}`}
              </Button>
            )}
            <div className="mt-4 text-xs text-neutral-500 space-y-1">
              <div className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-[#8247E5]" /> Instant delivery via email</div>
              <div className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-[#8247E5]" /> Powered by NOWPayments</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;

// ─── PaymentPanel ─────────────────────────────────────────────────────────
// Premium purple payment card shown at step 2. Live 4-stage status pipeline
// (Waiting → Detected → Confirming → Paid), animated laser-scan over the QR,
// and a real-time mempool monitor. Preserves all data-testids used by tests.

const STAGE_MAP = {
  pending_payment: { stage: 0, headline: 'Send crypto to complete your order', tone: 'violet', desc: "Send the exact amount below to the address on the right. We're watching the mempool in real time. The moment your transaction shows up you'll see it here." },
  waiting:         { stage: 0, headline: 'Send crypto to complete your order', tone: 'violet', desc: "Send the exact amount below to the address on the right. We're watching the mempool in real time." },
  sending:         { stage: 1, headline: 'Transaction detected on-chain',     tone: 'sky',    desc: 'We saw your transaction in the mempool. Waiting for the first confirmation.' },
  confirming:      { stage: 2, headline: 'Confirming on the network',         tone: 'amber',  desc: 'Your transaction is being confirmed. Typically 1–3 blocks depending on the network.' },
  partially_paid:  { stage: 1, headline: 'Partial payment received',          tone: 'orange', desc: 'We got part of your payment. Send the remaining amount to the same address below to complete your order.' },
  paid:            { stage: 3, headline: "You're all set!",                   tone: 'green',  desc: 'Your gift codes are being generated. You\'ll receive them by email in a few seconds.' },
  delivered:       { stage: 3, headline: 'Codes delivered',                   tone: 'green',  desc: 'All done. Check your inbox. Codes are also available in your account.' },
  expired:         { stage: 0, headline: 'Payment address expired',           tone: 'rose',   desc: 'The address is no longer valid. Start a new order to get a fresh address.' },
  failed:          { stage: 0, headline: 'Payment failed',                    tone: 'rose',   desc: 'Something went wrong. Contact support with your order ID and we\'ll get it sorted.' },
};

const TONE_GRADIENTS = {
  violet: 'from-violet-500 to-fuchsia-500',
  sky:    'from-sky-500 to-cyan-500',
  amber:  'from-amber-500 to-orange-500',
  orange: 'from-orange-500 to-amber-500',
  green:  'from-emerald-500 to-green-500',
  rose:   'from-rose-500 to-red-500',
};

const PaymentPanel = ({ order, status, method, isPartial, actuallyPaid, expectedPay, remainingCrypto, copied, onCopy, formatPrice, isUsd }) => {
  const currentStatus = status?.status || order?.status || 'pending_payment';
  const meta = STAGE_MAP[currentStatus] || STAGE_MAP.pending_payment;
  const cryptoLabel = (order?.payment_pay_currency || method).toUpperCase();
  const cryptoAmount = isPartial ? remainingCrypto : (order?.payment_amount_crypto ?? 0);

  if (!order?.payment_address) {
    return (
      <div className="rounded-3xl p-12 text-center" style={{ background: 'linear-gradient(135deg, #1B0A38 0%, #0F0522 100%)', boxShadow: '0 30px 80px -20px rgba(130,71,229,0.35)' }}>
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#C084FC]" />
        <div className="text-white font-semibold">Preparing your payment address…</div>
        <div className="text-xs text-white/50 mt-1">This usually takes a second or two.</div>
      </div>
    );
  }

  return (
    <div className="relative rounded-3xl overflow-hidden" style={{
      background: 'linear-gradient(135deg, #1B0A38 0%, #0F0522 100%)',
      boxShadow: '0 30px 80px -20px rgba(130,71,229,0.5), 0 0 0 1px rgba(130,71,229,0.2) inset',
    }}>
      {/* Ambient blobs */}
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-40 pointer-events-none" style={{ background: '#8247E5' }} />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: '#A855F7' }} />

      <div className="relative grid md:grid-cols-2 gap-8 p-6 md:p-10">

        {/* LEFT — Status + pipeline + monitor */}
        <div className="space-y-6">
          <div data-testid="payment-status-pill" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest"
               style={{
                 background: 'linear-gradient(135deg, rgba(130,71,229,0.18), rgba(192,132,252,0.14))',
                 color: '#F5F3FF',
                 border: '1px solid rgba(255,255,255,0.12)',
               }}>
            <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${TONE_GRADIENTS[meta.tone]}`} style={{ animation: 'tg-status-pulse 1.5s ease-in-out infinite' }} />
            {meta.headline}
          </div>

          <h2 className="text-3xl md:text-4xl font-display font-bold leading-tight" style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #C084FC 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            {meta.headline}
          </h2>
          <p className="text-sm text-white/70 leading-relaxed">{meta.desc}</p>

          {/* Progress pipeline */}
          <div>
            <div className="grid grid-cols-4 gap-2">
              {['Waiting', 'Detected', 'Confirming', 'Paid'].map((s, i) => {
                const done = i < meta.stage;
                const active = i === meta.stage;
                return (
                  <div key={s} className="flex flex-col items-start gap-2">
                    <div className={`h-1 w-full rounded-full transition-all duration-500 ${done || active ? 'bg-gradient-to-r from-violet-500 to-fuchsia-400' : 'bg-white/10'}`}
                         style={active ? { animation: 'tg-status-pulse 1.5s ease-in-out infinite' } : {}} />
                    <div className={`text-[10px] uppercase tracking-widest ${done || active ? 'text-white' : 'text-white/40'}`}>{s}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Partial-payment banner (only when partial) */}
          {isPartial && remainingCrypto > 0 && (
            <div data-testid="partial-payment-banner" className="rounded-2xl border border-orange-400/40 bg-orange-500/10 p-4">
              <div className="text-[10px] font-bold text-orange-300 uppercase tracking-wider mb-1">Partial payment received</div>
              <div className="text-xs text-white/80 leading-relaxed">
                We received <b className="text-white">{actuallyPaid} {cryptoLabel}</b> but need <b className="text-white">{expectedPay} {cryptoLabel}</b>. Send the remaining amount to the same address to complete your order.
              </div>
              <div className="mt-3 pt-3 border-t border-orange-400/20">
                <div className="text-[10px] font-semibold text-orange-300 uppercase mb-0.5">Send the remaining</div>
                <div className="font-display text-2xl font-bold text-white" data-testid="remaining-amount">
                  {remainingCrypto} <span className="text-base font-normal text-white/60">{cryptoLabel}</span>
                </div>
              </div>
            </div>
          )}

          {/* Real-time monitor */}
          <div className="rounded-2xl border border-violet-500/25 bg-black/25 p-4 backdrop-blur">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-white/50">Real-time payment monitor</div>
              <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ animation: 'tg-pulse 1.4s ease-in-out infinite' }} />
                LIVE · 1s polling
              </div>
            </div>
            <div className="font-mono text-[11px] text-white/70 space-y-1">
              <div className="flex justify-between"><span>Network</span><span className="text-white">{cryptoLabel}</span></div>
              <div className="flex justify-between"><span>Status</span><span className="text-white">{currentStatus.replace('_', ' ')}</span></div>
              {order?.payment_expires_at && (
                <div className="flex justify-between"><span>Address expires</span><span className="text-white">{new Date(order.payment_expires_at).toLocaleTimeString()}</span></div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT — QR + amount + address */}
        <div className="space-y-4">
          <div className="relative rounded-2xl bg-white p-5">
            {/* Laser scan overlay */}
            <div className="absolute inset-x-5 top-5 bottom-5 overflow-hidden pointer-events-none rounded-lg">
              <div className="h-1/4 w-full" style={{ background: 'linear-gradient(180deg, transparent, rgba(130,71,229,0.35), transparent)', animation: 'tg-scan 2.4s ease-in-out infinite' }} />
            </div>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(order.payment_address)}&margin=0`}
              alt="QR" className="w-full aspect-square" />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Amount to send</div>
            <div className="font-display text-3xl font-bold text-white leading-none" data-testid="payment-amount-crypto">
              {cryptoAmount} <span className="text-base text-white/50 font-normal">{cryptoLabel}</span>
            </div>
            <div className="text-xs text-white/50 mt-1">
              ≈ {formatPrice(order.subtotal_usd)}{!isUsd ? ` (~$${order.subtotal_usd.toFixed(2)} USD)` : ''}{isPartial ? ' (remaining after partial payment)' : ''}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Deposit address</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 font-mono text-[11px] text-white/85 bg-black/30 border border-violet-500/20 rounded-lg px-3 py-2 break-all" data-testid="payment-address">{order.payment_address}</div>
              <button data-testid="copy-address-btn" onClick={() => onCopy(order.payment_address)} className="h-9 w-9 rounded-lg bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/30 flex items-center justify-center transition-colors">
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-white/80" />}
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-violet-500/25 bg-violet-500/10 px-3 py-2.5 text-[11px] text-white/85 flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-[#C084FC] shrink-0 mt-0.5" />
            <span>Codes are emailed to <b className="text-white">{order.email}</b> the moment your payment is confirmed. This page updates in real time. No refresh needed.</span>
          </div>
          <div className="text-[10px] text-white/50 flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" /> Send only <b className="text-white/80">{cryptoLabel}</b> to this address. Sending any other coin will result in loss of funds.
          </div>
        </div>
      </div>
    </div>
  );
};
